import React, { useEffect } from 'react';

export interface ToastProps {
  /** Message to show; when null/empty the toast is hidden */
  message: string | null;
  /** Called when the toast is closed (dismiss or auto-close) */
  onDismiss: () => void;
  /** Auto-close after this many ms; set to 0 to disable */
  autoCloseMs?: number;
  /** Visual variant */
  variant?: 'default' | 'success' | 'error';
  /** Position on screen */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const DEFAULT_AUTO_CLOSE_MS = 3000;

const Toast: React.FC<ToastProps> = ({
  message,
  onDismiss,
  autoCloseMs = DEFAULT_AUTO_CLOSE_MS,
  variant = 'default',
  position = 'top-right',
}) => {
  useEffect(() => {
    if (!message || autoCloseMs <= 0) return;
    const t = setTimeout(onDismiss, autoCloseMs);
    return () => clearTimeout(t);
  }, [message, autoCloseMs, onDismiss]);

  if (!message?.trim()) return null;

  return (
    <div
      className={`toast-notification toast-notification--${position} toast-notification--${variant}`}
      role="status"
      aria-live="polite"
    >
      <span className="toast-notification__message">
        {variant === 'success' && <i className="bi bi-check-circle-fill me-2" />}
        {variant === 'error' && <i className="bi bi-exclamation-circle-fill me-2" />}
        {message}
      </span>
      <button
        type="button"
        className="toast-notification__close"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
};

export default Toast;
